import Schedule from "@mui/icons-material/Schedule";
import { Badge, Box, IconButton, Tooltip as MuiTooltip, styled, Typography } from "@mui/material";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InformationalPopup } from "@/components/InformationalPopup/InformationalPopup";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSchedulePublishReminder } from "@/hooks/useSchedulePublishReminder";
import { INK, STATUS, SURFACE } from "@/theme/tokens";

const PulseBadge = styled(Badge)({
	"& .MuiBadge-badge": {
		backgroundColor: STATUS.ERROR.main,
		color: STATUS.ERROR.main,
		width: 10,
		height: 10,
		minWidth: 10,
		borderRadius: "50%",
		border: `2px solid ${SURFACE}`,
		padding: 0,
		animation: "pulse-dot 1.5s ease-in-out infinite",
	},
	"@keyframes pulse-dot": {
		"0%, 100%": { transform: "scale(1)", opacity: 1 },
		"50%": { transform: "scale(1.3)", opacity: 0.8 },
	},
});

interface Props {
	onClick: () => void;
	dataTestId?: string;
}

export const ScheduleVisibilityButton: FC<Props> = ({ onClick, dataTestId }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { level, daysRemaining } = useSchedulePublishReminder(chosenDepartment?.staffScheduleDisplayUntilDate);
	const [badgeSeen, setBadgeSeen] = useState(false);
	const [popupDismissed, setPopupDismissed] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const dateKey = chosenDepartment?.staffScheduleDisplayUntilDate?.toISODate() ?? "";
	const storageKey = `schedule_reminder_seen_${chosenDepartment?.department_id}_${dateKey}`;
	const popupStorageKey = `schedule_reminder_popup_dismissed_${chosenDepartment?.department_id}_${dateKey}`;

	useEffect(() => {
		if (level !== "none" && chosenDepartment?.department_id) {
			const seen = localStorage.getItem(storageKey);
			setBadgeSeen(seen === "true");

			const dismissed = localStorage.getItem(popupStorageKey);
			setPopupDismissed(dismissed === "true");
		} else {
			// Reset when not in reminder range
			setBadgeSeen(false);
			setPopupDismissed(false);
			if (chosenDepartment?.department_id) {
				localStorage.removeItem(storageKey);
				localStorage.removeItem(popupStorageKey);
			}
		}
	}, [level, chosenDepartment?.department_id, storageKey, popupStorageKey]);

	const handleClick = useCallback(() => {
		setBadgeSeen(true);
		setPopupDismissed(true);
		if (chosenDepartment?.department_id) {
			localStorage.setItem(storageKey, "true");
			localStorage.setItem(popupStorageKey, "true");
		}
		onClick();
	}, [onClick, chosenDepartment?.department_id, storageKey, popupStorageKey]);

	const handlePopupClose = useCallback(() => {
		setPopupDismissed(true);
		if (chosenDepartment?.department_id) {
			localStorage.setItem(popupStorageKey, "true");
		}
	}, [chosenDepartment?.department_id, popupStorageKey]);

	if (!chosenDepartment?.staffScheduleDisplayUntilDate) {
		return null;
	}

	const showBadge = level !== "none" && !badgeSeen;
	const showPopup = level === "urgent" && !popupDismissed;

	const getReminderMessage = (): string => {
		if (daysRemaining === null) return "";
		if (daysRemaining <= 0) {
			return t("schedule.reminder.expired");
		}
		return t("schedule.reminder.days.remaining", { days: daysRemaining });
	};

	const iconButton = (
		<MuiTooltip title={t("staff.schedule.visibility")}>
			<IconButton ref={buttonRef} onClick={handleClick} data-testid={dataTestId}>
				{showBadge ? (
					<PulseBadge variant="dot" overlap="circular" anchorOrigin={{ vertical: "top", horizontal: "right" }}>
						<Schedule sx={{ color: level === "urgent" ? STATUS.ERROR.main : "primary.main" }} />
					</PulseBadge>
				) : (
					<Schedule sx={{ color: "primary.main" }} />
				)}
			</IconButton>
		</MuiTooltip>
	);

	if (showPopup) {
		return (
			<InformationalPopup
				isOpen={showPopup}
				title={t("schedule.reminder.title")}
				content={
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
						<Typography variant="body2" sx={{ color: INK.MUTED, fontSize: "13px", textAlign: "center" }}>
							{getReminderMessage()}
						</Typography>
					</Box>
				}
				onClose={handlePopupClose}
				positions={["bottom", "top", "left", "right"]}
				align="center"
			>
				{iconButton}
			</InformationalPopup>
		);
	}

	return iconButton;
};
